import React, { useState, type SyntheticEvent } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { Box, Grid, InputAdornment, Link, Stack } from '@mui/material';
import { Calculate } from '@mui/icons-material';
import { useFormik } from 'formik';
import { boolean, number, object, string } from 'yup';
import {
  worldUniversityRankingOptions,
  worldUniversityRankingRangeOptions,
  worldSubjectRankingRangeOptions,
  disciplineRankingOptions,
  rankingLinks,
  NationalRankingOptions,
} from './options';
import type { AcademicAssessment, WorldUniversityRanking } from './types';
import {
  Ranking,
  WorldUniversityRankingRange,
  NationalRankingSystem,
  WorldSubjectRankingRange,
  DisciplineRankingSystem,
} from '../../../../DRCAssessment';
import { PAGE_TEXT } from '../../../../lang';
import {
  TextField,
  Button,
  Checkbox,
  Select,
  UploadBox,
  FILE_MANAGE_ORDER,
  type FileData,
  IconButton,
} from '../../../../../../components';
import {
  type ConvertGPADialogValue,
  GPAConversionDialogV2,
} from '../../../../../Application/components';
import { PAGE_TEXT as Dialog_Text } from '../../../../../Application/components/ApplicationForm/lang';
import { type Mark } from '../../../../../Application/graphql';

type AcademicAssessmentsContentProps = {
  initialAssessmentValue: AcademicAssessment;
  isViewMode: boolean;
  onJumpToNext?: () => void;
  onSubmit: (values: AcademicAssessment) => void;
  GPAConvertDisplay: {
    marks: Mark[];
    convertGPARuleId: string;
    institutionName: string;
    countryRegion: string;
    gpaIndicator: boolean;
  };
  dialogField: {
    officialMark: string;
    outOfGpa: string;
    convertedMark: string;
  };
};

const STEP_TEXT = PAGE_TEXT.DRC_ASSESSMENT_DETAILS.ASSESSMENT.STEP_A;
const dialogText = Dialog_Text.QUALIFICATION.ACADEMIC_QUALIFICATION.GPA_DIALOG;
const presentHKPFS = process.env.REACT_APP_HKPFS_ON === 'true';

export const AcademicAssessmentsContent: React.FunctionComponent<
  AcademicAssessmentsContentProps
> = props => {
  const { applicationId } = useParams();
  const {
    initialAssessmentValue,
    isViewMode,
    GPAConvertDisplay,
    dialogField,
    onSubmit: handleSubmit,
    onJumpToNext,
  } = props;

  const { institutionName, countryRegion, convertGPARuleId, gpaIndicator } =
    GPAConvertDisplay;
  const { officialMark, outOfGpa, convertedMark } = dialogField;
  const [openDialog, setOpenDialog] = useState(false);
  const [proof, setProof] = useState<FileData[]>(
    initialAssessmentValue.nationalRanking?.proof
      ? [initialAssessmentValue.nationalRanking?.proof]
      : [],
  );

  const { worldUniversityRanking, ...other } = initialAssessmentValue;

  const {
    score = '',
    universityLevel = '',
    ranking = '',
    range = '',
    rankNumber,
    notExactNumber = false,
  } = worldUniversityRanking ?? {};

  const processedWorldUniversityRanking: WorldUniversityRanking = {
    score,
    universityLevel,
    ranking,
    range,
    rankNumber:
      rankNumber === -1 || rankNumber === undefined ? null : rankNumber,
    notExactNumber,
  };

  const processedInitialValues: AcademicAssessment = {
    worldUniversityRanking: processedWorldUniversityRanking,
    ...other,
  };

  const formik = useFormik<AcademicAssessment>({
    initialValues: processedInitialValues,
    validationSchema: object({
      title: string(),
      convertedGPA: number()
        .required('Required')
        .test({
          message: 'Out of range',
          test: value => value >= 0 && value <= 4,
        }),
      selectWorldUniversityRanking: boolean().required('Required'),
      worldUniversityRanking: object().when('selectWorldUniversityRanking', {
        is: (selectWorldUniversityRanking: boolean) =>
          selectWorldUniversityRanking,
        then: schema =>
          schema.shape({
            score: number().required('Required'),
            universityLevel: number().required('Required'),
            ranking: string().required('Required'),
            range: string().required('Required'),
            rankNumber: number().when('notExactNumber', {
              is: (notExactNumber: boolean) => !notExactNumber,
              then: schema =>
                schema.required('Required').test({
                  message: 'Invalid',
                  test: value => value > 0,
                }),
              otherwise: schema => schema.notRequired(),
            }),
            notExactNumber: boolean().required('Required'),
          }),
        otherwise: schema =>
          schema.shape({
            score: number(),
            universityLevel: number(),
            ranking: string(),
            range: string(),
            rankNumber: number().nullable(),
            notExactNumber: boolean(),
          }),
      }),

      selectNationalRanking: boolean().required('Required'),
      nationalRanking: object().when('selectNationalRanking', {
        is: (selectNationalRanking: boolean) => selectNationalRanking,
        then: schema =>
          schema.shape({
            score: number().required('Required'),
            universityLevel: number().required('Required'),
            ranking: string().required('Required'),
          }),
        otherwise: schema =>
          schema.shape({
            score: number(),
            universityLevel: number(),
            ranking: string(),
          }),
      }),

      selectWorldSubjectRanking: boolean().required('Required'),
      worldSubjectRanking: object().when('selectWorldSubjectRanking', {
        is: (selectWorldSubjectRanking: boolean) => selectWorldSubjectRanking,
        then: schema =>
          schema.shape({
            score: number().required('Required'),
            universityLevel: number().required('Required'),
            ranking: string().required('Required'),
            range: string().required('Required'),
            subjectArea: string().required('Required'),
          }),
        otherwise: schema =>
          schema.shape({
            score: number(),
            universityLevel: number(),
            ranking: string(),
            range: string(),
            subjectArea: string(),
          }),
      }),

      selectDisciplineSpecificList: boolean().required('Required'),
      disciplineSpecificList: object().when('selectDisciplineSpecificList', {
        is: (selectDisciplineSpecificList: boolean) =>
          selectDisciplineSpecificList,
        then: schema =>
          schema.shape({
            score: number().required('Required'),
            universityLevel: number().required('Required'),
            ranking: string().required('Required'),
            discipline: string().required('Required'),
          }),
        otherwise: schema =>
          schema.shape({
            score: number(),
            universityLevel: number(),
            ranking: string(),
            discipline: string(),
          }),
      }),
      notApplicable: boolean().required('Required'),
    }),
    onSubmit: value => {
      const {
        selectWorldUniversityRanking,
        selectNationalRanking,
        selectWorldSubjectRanking,
        selectDisciplineSpecificList,
        notApplicable,
      } = value;
      if (
        !(
          selectWorldUniversityRanking ||
          selectNationalRanking ||
          selectWorldSubjectRanking ||
          selectDisciplineSpecificList ||
          notApplicable
        )
      ) {
        formik.setErrors({
          ...formik.errors,
          selectWorldUniversityRanking: 'Required',
          selectNationalRanking: 'Required',
          selectWorldSubjectRanking: 'Required',
          selectDisciplineSpecificList: 'Required',
          notApplicable: 'Required',
        });
        return;
      }

      handleSubmit(value);
    },
  });

  const notRanked: boolean = formik.values.notApplicable;

  function handleProofDocsChange(order: number, file: FileData): boolean {
    const processingFileName = `${applicationId}_national_rank_proof`;
    let newProof: FileData[] = proof;
    switch (order) {
      case FILE_MANAGE_ORDER.UPLOAD:
        newProof = [
          {
            id: file.id,
            link: file.link,
            name: processingFileName,
          },
        ];
        break;
      case FILE_MANAGE_ORDER.REMOVE:
        newProof = [];
        break;
      default:
    }
    setProof(newProof);
    void formik.setFieldValue('nationalRanking.proof', newProof[0]);
    return true;
  }

  const handleWorldUniversityRankingChange = async (
    event: React.ChangeEvent<{ value: string }>,
  ): Promise<void> => {
    const value = event.target.value;
    await formik.setFieldValue('worldUniversityRanking.range', value);

    let score = 0;
    let universityLevel = 0;

    switch (value) {
      case WorldUniversityRankingRange.TOP_1_TO_50:
        score = 7;
        universityLevel = 5;
        break;
      case WorldUniversityRankingRange.TOP_51_TO_100:
      case WorldUniversityRankingRange.TOP_101_TO_200:
        score = 5;
        universityLevel = 4;
        break;
      case WorldUniversityRankingRange.TOP_201_TO_300:
        score = 3;
        universityLevel = 3;
        break;
      case WorldUniversityRankingRange.TOP_301_TO_400:
        score = 2;
        universityLevel = 2;
        break;
      case WorldUniversityRankingRange.TOP_401_TO_500:
        score = 1;
        universityLevel = 1;
        break;
      default:
    }
    await Promise.all([
      formik.setFieldValue('worldUniversityRanking.score', score),
      formik.setFieldValue(
        'worldUniversityRanking.universityLevel',
        universityLevel,
      ),
    ]);
  };

  const handleNationalRankingChange = async (
    event: React.ChangeEvent<{ value: string }>,
  ): Promise<void> => {
    const value = event.target.value;
    await formik.setFieldValue('nationalRanking.ranking', value);

    let score = 0;
    let universityLevel = 0;

    switch (value) {
      case NationalRankingSystem.FIRST_TIER:
        score = 7;
        universityLevel = 5;
        break;
      case NationalRankingSystem.SECOND_TIER:
        score = 5;
        universityLevel = 4;
        break;
      default:
    }
    await Promise.all([
      formik.setFieldValue('nationalRanking.score', score),
      formik.setFieldValue('nationalRanking.universityLevel', universityLevel),
    ]);
  };

  const handleWorldSubjectRankingChange = async (
    event: React.ChangeEvent<{ value: string }>,
  ): Promise<void> => {
    const value = event.target.value;
    await formik.setFieldValue('worldSubjectRanking.range', value);

    let score = 0;
    let universityLevel = 0;

    switch (value) {
      case WorldSubjectRankingRange.TOP_1_TO_20:
        score = 7;
        universityLevel = 5;
        break;
      case WorldSubjectRankingRange.TOP_21_TO_50:
        score = 5;
        universityLevel = 4;
        break;
      case WorldSubjectRankingRange.TOP_51_TO_75:
        score = 3;
        universityLevel = 3;
        break;
      case WorldSubjectRankingRange.TOP_76_TO_100:
        score = 2;
        universityLevel = 2;
        break;
      case WorldSubjectRankingRange.TOP_101_TO_150:
        score = 1;
        universityLevel = 1;
        break;
      default:
    }
    await Promise.all([
      formik.setFieldValue('worldSubjectRanking.score', score),
      formik.setFieldValue(
        'worldSubjectRanking.universityLevel',
        universityLevel,
      ),
    ]);
  };

  const handleDisciplineSpecificListChange = async (
    event: React.ChangeEvent<{ value: string }>,
  ): Promise<void> => {
    const value = event.target.value;
    await formik.setFieldValue('disciplineSpecificList.ranking', value);

    let score = 0;
    let universityLevel = 0;

    switch (value) {
      case DisciplineRankingSystem.UL1:
        score = 1;
        universityLevel = 1;
        break;
      case DisciplineRankingSystem.UL2:
        score = 2;
        universityLevel = 2;
        break;
      case DisciplineRankingSystem.UL3:
        score = 3;
        universityLevel = 3;
        break;
      case DisciplineRankingSystem.UL4:
        score = 5;
        universityLevel = 4;
        break;
      case DisciplineRankingSystem.UL5:
        score = 7;
        universityLevel = 5;
        break;
      default:
    }
    await Promise.all([
      formik.setFieldValue('disciplineSpecificList.score', score),
      formik.setFieldValue(
        'disciplineSpecificList.universityLevel',
        universityLevel,
      ),
    ]);
  };

  const handleNoRanked = async (
    event: SyntheticEvent<Element, Event>,
    checked: boolean,
  ): Promise<void> => {
    if (checked) {
      await Promise.all([
        formik.setFieldValue('selectWorldUniversityRanking', false),
        formik.setFieldValue('selectNationalRanking', false),
        formik.setFieldValue('selectWorldSubjectRanking', false),
        formik.setFieldValue('selectDisciplineSpecificList', false),
        formik.setFieldValue('notApplicable', true),
      ]);
    } else {
      await formik.setFieldValue('notApplicable', false);
    }
  };

  const WORLDRANKING_LABEL = STEP_TEXT.WOLRD_UNIVERSITY_RANKING_CONTENT;
  const NATIONALRANKING_LABEL = STEP_TEXT.NATIONAL_RANKING_CONTENT;
  const WORLDSUBJECTRANKING_LABEL = STEP_TEXT.WORLD_SUBJECTRANKING_CONTENT;
  const DISCIPLINE_SPECIFIC_LIST_CONTENT =
    STEP_TEXT.DISCIPLINE_SPECIFIC_LIST_CONTENT;

  const RankingLinks: React.FC = () => {
    return (
      <>
        {rankingLinks.map((ranking, index) => (
          <small className="text-[#777777]" key={index}>
            {ranking.name}:{' '}
            <Link href={ranking.url} target="_blank" rel="noopener">
              {ranking.url}
            </Link>
            <br />
          </small>
        ))}
      </>
    );
  };

  const handleOpenDialog = (): void => {
    setOpenDialog(true);
  };

  const handleDialogClose = (): void => {
    setOpenDialog(false);
  };

  const handleDialogApply = async (
    dialogContent: ConvertGPADialogValue,
  ): Promise<void> => {
    await formik.setFieldValue(
      'convertedGPA',
      dialogContent.convertedGPA.toString(),
    );
    setOpenDialog(false);
  };

  return (
    <ContentBox>
      <form onSubmit={formik.handleSubmit} noValidate>
        <Stack>
          {openDialog && (
            <GPAConversionDialogV2
              viewMode={true}
              dialogTitle={dialogText.DIALOG_V2_TITLE}
              openDialog={openDialog}
              onDialogClose={handleDialogClose}
              onDialogApply={handleDialogApply}
              institutionName={institutionName}
              countryRegion={countryRegion}
              convertGPARuleId={convertGPARuleId}
              officialMark={Number(officialMark) ?? 0}
              outOfGpa={Number(outOfGpa) ?? 100}
              convertedMark={Number(convertedMark ?? 0)}
            />
          )}
          <div className="grid grid-cols-2 gap-4 place-content-start">
            <h2 style={{ marginTop: '0.1rem' }}>{STEP_TEXT.GPA.TITLE}</h2>
            <div className="w-full h-[60px]">
              <TextField
                id="convertedGPA"
                name="convertedGPA"
                disabled={isViewMode}
                value={formik.values.convertedGPA}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                type="number"
                className="w-1/2"
                inputProps={{
                  min: 0,
                  max: 4,
                  step: 0.01,
                }}
                InputProps={{
                  readOnly: !gpaIndicator,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        color="primary"
                        size="small"
                        disabled={isViewMode || gpaIndicator}
                        title={STEP_TEXT.GPA.VIEW_GPA_CONVERSION}
                        icon={
                          <Calculate
                            sx={{
                              color: gpaIndicator ? '#8b8b8b' : 'inherit',
                            }}
                          />
                        }
                        onClick={handleOpenDialog}
                      />
                    </InputAdornment>
                  ),
                }}
                error={
                  formik.touched.convertedGPA &&
                  Boolean(formik.errors.convertedGPA)
                }
                helperText={
                  formik.touched.convertedGPA && formik.errors.convertedGPA
                }
                size="small"
                style={{
                  marginTop: '-0.25rem',
                  marginRight: '0.5rem',
                }}
              />
              <span>{STEP_TEXT.GPA.OUT_OF_4}</span>
            </div>
          </div>
          {presentHKPFS && (
            <h2 className="mt-4 font-bold text-[#951D1D] text-sm">
              {STEP_TEXT.HKPFS_NOTE}
            </h2>
          )}
          {/* World University Ranking */}
          <Box className="mt-4">
            <Checkbox
              disabled={isViewMode || notRanked}
              label={Ranking.WORLD_RANKING}
              value={formik.values.selectWorldUniversityRanking}
              name="selectWorldUniversityRanking"
              error={Boolean(formik.errors.selectWorldUniversityRanking)}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.values.selectWorldUniversityRanking && (
              <Box marginLeft={4}>
                <RankingLinks />
                <Grid
                  className="mt-4"
                  container
                  columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                  rowGap={2}
                >
                  <Grid container justifyContent="space-between" size={6}>
                    {WORLDRANKING_LABEL.RANKING}
                    <Select
                      disabled={isViewMode}
                      id="worldUniversityRanking.ranking"
                      name="worldUniversityRanking.ranking"
                      value={formik.values.worldUniversityRanking?.ranking}
                      InputLabelProps={{ shrink: true }}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      options={worldUniversityRankingOptions}
                      error={
                        Boolean(
                          (formik.touched.worldUniversityRanking as any)
                            ?.ranking,
                        ) &&
                        Boolean(
                          (formik.errors.worldUniversityRanking as any)
                            ?.ranking,
                        )
                      }
                      helperText={
                        Boolean(
                          (formik.touched.worldUniversityRanking as any)
                            ?.ranking,
                        ) &&
                        (formik.errors.worldUniversityRanking as any)?.ranking
                      }
                      size="small"
                      sx={{ width: '150px' }}
                    />
                  </Grid>
                  <Grid container justifyContent="space-between" size={6}>
                    {WORLDRANKING_LABEL.UNIVERSITY_RANKING}
                    <Select
                      disabled={isViewMode}
                      id="worldUniversityRanking.range"
                      name="worldUniversityRanking.range"
                      value={formik.values.worldUniversityRanking?.range}
                      InputLabelProps={{ shrink: true }}
                      onChange={handleWorldUniversityRankingChange}
                      onBlur={formik.handleBlur}
                      error={
                        Boolean(
                          (formik.touched.worldUniversityRanking as any)?.range,
                        ) &&
                        Boolean(
                          (formik.errors.worldUniversityRanking as any)?.range,
                        )
                      }
                      helperText={
                        Boolean(
                          (formik.touched.worldUniversityRanking as any)
                            ?.universityRanking,
                        ) &&
                        (formik.errors.worldUniversityRanking as any)
                          ?.universityRanking
                      }
                      options={worldUniversityRankingRangeOptions}
                      size="small"
                      sx={{ width: '150px' }}
                    />
                  </Grid>

                  <Grid
                    container
                    justifyContent="space-between"
                    height={40}
                    size={6}
                  >
                    {WORLDRANKING_LABEL.EXACT_RANKING_NUM}
                    <TextField
                      id="worldUniversityRanking.rankNumber"
                      name="worldUniversityRanking.rankNumber"
                      value={
                        formik.values.worldUniversityRanking?.notExactNumber
                          ? ''
                          : formik.values.worldUniversityRanking?.rankNumber
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      InputLabelProps={{ shrink: true }}
                      type="number"
                      inputProps={{
                        min: 0,
                        step: 1,
                      }}
                      error={
                        Boolean(
                          (formik.touched.worldUniversityRanking as any)
                            ?.rankNumber,
                        ) &&
                        Boolean(
                          (formik.errors.worldUniversityRanking as any)
                            ?.rankNumber,
                        )
                      }
                      helperText={
                        Boolean(
                          (formik.touched.worldUniversityRanking as any)
                            ?.rankNumber,
                        ) &&
                        (formik.errors.worldUniversityRanking as any)
                          ?.rankNumber
                      }
                      size="small"
                      disabled={
                        Boolean(isViewMode) ||
                        formik.values.worldUniversityRanking?.notExactNumber
                      }
                      sx={{
                        width: '150px',
                        background: formik.values.worldUniversityRanking
                          ?.notExactNumber
                          ? '#E6E6E6'
                          : 'inherit',
                      }}
                    />
                  </Grid>
                  <Grid
                    container
                    justifyContent="space-between"
                    alignItems="flex-start"
                    size={6}
                  >
                    {WORLDRANKING_LABEL.SCORE}
                    <TextField
                      id="worldUniversityRanking.score"
                      name="worldUniversityRanking.score"
                      value={formik.values.worldUniversityRanking?.score}
                      size="small"
                      disabled
                      inputProps={{
                        style: { textAlign: 'right' },
                      }}
                      sx={{
                        width: '150px',
                        background: '#E6E6E6',
                      }}
                    />
                  </Grid>
                  <Grid container justifyContent="flex-end" size={6}>
                    <Checkbox
                      value={
                        formik.values.worldUniversityRanking?.notExactNumber ??
                        false
                      }
                      disabled={isViewMode}
                      name="worldUniversityRanking.notExactNumber"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label={WORLDRANKING_LABEL.NO_EXACT_RANKING_NUMBER}
                      sx={{
                        marginTop: '-0.75rem',
                        marginRight: '-0.3rem',
                        '& .MuiSvgIcon-root': { fontSize: 16 },
                      }}
                    />
                  </Grid>
                  <Grid container justifyContent="space-between" size={6}>
                    {WORLDRANKING_LABEL.UL}
                    <TextField
                      id="worldUniversityRanking.universityLevel"
                      name="worldUniversityRanking.universityLevel"
                      value={
                        formik.values.worldUniversityRanking?.universityLevel
                      }
                      size="small"
                      inputProps={{
                        style: { textAlign: 'right' },
                      }}
                      disabled
                      sx={{ width: '150px', background: '#E6E6E6' }}
                    />
                  </Grid>
                </Grid>
                <hr />
              </Box>
            )}
          </Box>
          {/* National Ranking */}
          <Box className="mt-4">
            <Checkbox
              disabled={isViewMode || notRanked}
              label={Ranking.NATIONAL_RANKING}
              value={formik.values.selectNationalRanking}
              name="selectNationalRanking"
              error={Boolean(formik.errors.selectNationalRanking)}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.values.selectNationalRanking && (
              <Box marginLeft={4}>
                <Grid
                  className="mt-4"
                  container
                  columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                  rowGap={2}
                >
                  <Grid container justifyContent="space-between" size={7}>
                    {NATIONALRANKING_LABEL.RANKING}
                    <Select
                      disabled={isViewMode}
                      id="nationalRanking.ranking"
                      name="nationalRanking.ranking"
                      value={formik.values.nationalRanking?.ranking}
                      onChange={handleNationalRankingChange}
                      onBlur={formik.handleBlur}
                      InputLabelProps={{ shrink: true }}
                      error={
                        Boolean(
                          (formik.touched.nationalRanking as any)?.ranking,
                        ) &&
                        Boolean((formik.errors.nationalRanking as any)?.ranking)
                      }
                      helperText={
                        Boolean(
                          (formik.touched.nationalRanking as any)?.ranking,
                        ) && (formik.errors.nationalRanking as any)?.ranking
                      }
                      options={NationalRankingOptions}
                      size="small"
                      sx={{ width: '300px' }}
                    />
                  </Grid>
                  <Grid container size={10}>
                    <Grid container size={5}>
                      <h2 className="mb-2">{NATIONALRANKING_LABEL.PROOF}</h2>
                    </Grid>
                    <Grid container size={12}>
                      <UploadBox
                        maximumNum={1}
                        files={proof}
                        handleFilesChange={handleProofDocsChange}
                      />
                    </Grid>
                  </Grid>
                  <Grid container justifyContent="space-between" size={7}>
                    {NATIONALRANKING_LABEL.SCORE}
                    <TextField
                      id="nationalRanking.score"
                      name="nationalRanking.score"
                      value={formik.values.nationalRanking?.score}
                      size="small"
                      disabled
                      inputProps={{
                        style: { textAlign: 'right' },
                      }}
                      sx={{ width: '300px', background: '#E6E6E6' }}
                    />
                  </Grid>
                  <Grid container justifyContent="space-between" size={7}>
                    {NATIONALRANKING_LABEL.UL}
                    <TextField
                      id="nationalRanking.universityLevel"
                      name="nationalRanking.universityLevel"
                      value={formik.values.nationalRanking?.universityLevel}
                      size="small"
                      disabled
                      inputProps={{
                        style: { textAlign: 'right' },
                      }}
                      sx={{ width: '300px', background: '#E6E6E6' }}
                    />
                  </Grid>
                </Grid>
                <hr />
              </Box>
            )}
          </Box>

          {/* World Subject Ranking */}
          <Box className="mt-4">
            <Checkbox
              disabled={isViewMode || notRanked}
              label={Ranking.WORLD_SUBJECT_RANKING}
              value={formik.values.selectWorldSubjectRanking}
              name="selectWorldSubjectRanking"
              onChange={formik.handleChange}
              error={Boolean(formik.errors.selectWorldSubjectRanking)}
              onBlur={formik.handleBlur}
            />
            {formik.values.selectWorldSubjectRanking && (
              <Box marginLeft={4}>
                <RankingLinks />
                <Grid
                  className="mt-4"
                  container
                  columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                  rowGap={2}
                >
                  <Grid container justifyContent="space-between" size={6}>
                    {WORLDSUBJECTRANKING_LABEL.RANKING}
                    <Select
                      disabled={isViewMode}
                      id="worldSubjectRanking.ranking"
                      name="worldSubjectRanking.ranking"
                      value={formik.values.worldSubjectRanking?.ranking}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      InputLabelProps={{ shrink: true }}
                      error={
                        Boolean(
                          (formik.touched.worldSubjectRanking as any)?.ranking,
                        ) &&
                        Boolean(
                          (formik.errors.worldSubjectRanking as any)?.ranking,
                        )
                      }
                      helperText={
                        Boolean(
                          (formik.touched.worldSubjectRanking as any)?.ranking,
                        ) && (formik.errors.worldSubjectRanking as any)?.ranking
                      }
                      options={worldUniversityRankingOptions}
                      size="small"
                      sx={{ width: '150px' }}
                    />
                  </Grid>
                  <Grid container justifyContent="space-between" size={6}>
                    {WORLDSUBJECTRANKING_LABEL.SUBJECT_RANKING}
                    <Select
                      disabled={isViewMode}
                      id="worldSubjectRanking.range"
                      name="worldSubjectRanking.range"
                      value={formik.values.worldSubjectRanking?.range}
                      onChange={handleWorldSubjectRankingChange}
                      onBlur={formik.handleBlur}
                      InputLabelProps={{ shrink: true }}
                      error={
                        Boolean(
                          (formik.touched.worldSubjectRanking as any)?.range,
                        ) &&
                        Boolean(
                          (formik.errors.worldSubjectRanking as any)?.range,
                        )
                      }
                      helperText={
                        Boolean(
                          (formik.touched.worldSubjectRanking as any)?.range,
                        ) && (formik.errors.worldSubjectRanking as any)?.range
                      }
                      options={worldSubjectRankingRangeOptions}
                      size="small"
                      sx={{ width: '150px' }}
                    />
                  </Grid>

                  <Grid container justifyContent="space-between" size={6}>
                    {WORLDSUBJECTRANKING_LABEL.SUBJECT_AREA}
                    <TextField
                      disabled={isViewMode}
                      id="worldSubjectRanking.subjectArea"
                      name="worldSubjectRanking.subjectArea"
                      value={formik.values.worldSubjectRanking?.subjectArea}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      InputLabelProps={{ shrink: true }}
                      error={
                        Boolean(
                          (formik.touched.worldSubjectRanking as any)
                            ?.subjectArea,
                        ) &&
                        Boolean(
                          (formik.errors.worldSubjectRanking as any)
                            ?.subjectArea,
                        )
                      }
                      helperText={
                        Boolean(
                          (formik.touched.worldSubjectRanking as any)
                            ?.subjectArea,
                        ) &&
                        (formik.errors.worldSubjectRanking as any)?.subjectArea
                      }
                      size="small"
                      sx={{ width: '150px' }}
                    />
                  </Grid>
                  <Grid
                    container
                    justifyContent="space-between"
                    alignItems="flex-start"
                    size={6}
                  >
                    {WORLDSUBJECTRANKING_LABEL.SCORE}
                    <TextField
                      id="worldSubjectRanking.score"
                      name="worldSubjectRanking.score"
                      value={formik.values.worldSubjectRanking?.score}
                      size="small"
                      disabled
                      inputProps={{
                        style: { textAlign: 'right' },
                      }}
                      sx={{ width: '150px', background: '#E6E6E6' }}
                    />
                  </Grid>
                  <Grid container size={6}></Grid>
                  <Grid container justifyContent="space-between" size={6}>
                    {WORLDSUBJECTRANKING_LABEL.UL}
                    <TextField
                      id="worldSubjectRanking.universityLevel"
                      name="worldSubjectRanking.universityLevel"
                      value={formik.values.worldSubjectRanking?.universityLevel}
                      size="small"
                      disabled
                      inputProps={{
                        style: { textAlign: 'right' },
                      }}
                      sx={{ width: '150px', background: '#E6E6E6' }}
                    />
                  </Grid>
                </Grid>
                <hr />
              </Box>
            )}
          </Box>

          {/* Discipline Specific List */}
          <Box className="mt-4">
            <Checkbox
              disabled={isViewMode || notRanked}
              label={Ranking.DISCIPLINE_SPECIFIC_LIST}
              value={formik.values.selectDisciplineSpecificList}
              error={Boolean(formik.errors.selectDisciplineSpecificList)}
              name="selectDisciplineSpecificList"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.values.selectDisciplineSpecificList && (
              <Box marginLeft={4}>
                <h2 className="text-[#777777]">
                  {DISCIPLINE_SPECIFIC_LIST_CONTENT.NOTE}
                </h2>
                <h3 className="text-[#951D1D] mt-2">
                  {' '}
                  {DISCIPLINE_SPECIFIC_LIST_CONTENT.REFER}
                </h3>
                <Grid
                  className="mt-4"
                  container
                  columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                  rowGap={2}
                >
                  <Grid container justifyContent="space-between" size={6}>
                    {DISCIPLINE_SPECIFIC_LIST_CONTENT.DISCIPLINE_AREA}
                    <TextField
                      disabled={isViewMode}
                      id="disciplineSpecificList.discipline"
                      name="disciplineSpecificList.discipline"
                      value={formik.values.disciplineSpecificList?.discipline}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      InputLabelProps={{ shrink: true }}
                      error={
                        Boolean(
                          (formik.touched.disciplineSpecificList as any)
                            ?.discipline,
                        ) &&
                        Boolean(
                          (formik.errors.disciplineSpecificList as any)
                            ?.discipline,
                        )
                      }
                      helperText={
                        Boolean(
                          (formik.touched.disciplineSpecificList as any)
                            ?.discipline,
                        ) &&
                        (formik.errors.disciplineSpecificList as any)
                          ?.discipline
                      }
                      size="small"
                      sx={{ width: '150px' }}
                    />
                  </Grid>

                  <Grid container justifyContent="space-between" size={6}>
                    {DISCIPLINE_SPECIFIC_LIST_CONTENT.RANKING}
                    <Select
                      disabled={isViewMode}
                      id="disciplineSpecificList.ranking"
                      name="disciplineSpecificList.ranking"
                      value={formik.values.disciplineSpecificList?.ranking}
                      onChange={handleDisciplineSpecificListChange}
                      onBlur={formik.handleBlur}
                      InputLabelProps={{ shrink: true }}
                      error={
                        Boolean(
                          (formik.touched.disciplineSpecificList as any)
                            ?.ranking,
                        ) &&
                        Boolean(
                          (formik.errors.disciplineSpecificList as any)
                            ?.ranking,
                        )
                      }
                      helperText={
                        Boolean(
                          (formik.touched.disciplineSpecificList as any)
                            ?.ranking,
                        ) &&
                        (formik.errors.disciplineSpecificList as any)?.ranking
                      }
                      options={disciplineRankingOptions}
                      size="small"
                      sx={{ width: '150px' }}
                    />
                  </Grid>
                  <Grid container justifyContent="space-between" size={6}>
                    {DISCIPLINE_SPECIFIC_LIST_CONTENT.SCORE}
                    <TextField
                      id="disciplineSpecificList.score"
                      name="disciplineSpecificList.score"
                      value={formik.values.disciplineSpecificList?.score}
                      size="small"
                      disabled
                      inputProps={{
                        style: { textAlign: 'right' },
                      }}
                      sx={{ width: '150px', background: '#E6E6E6' }}
                    />
                  </Grid>
                  <Grid container justifyContent="space-between" size={6}>
                    {DISCIPLINE_SPECIFIC_LIST_CONTENT.UL}
                    <TextField
                      id="disciplineSpecificList.universityLevel"
                      name="disciplineSpecificList.universityLevel"
                      value={
                        formik.values.disciplineSpecificList?.universityLevel
                      }
                      size="small"
                      disabled
                      inputProps={{
                        style: { textAlign: 'right' },
                      }}
                      sx={{ width: '150px', background: '#E6E6E6' }}
                    />
                  </Grid>
                </Grid>
                <hr />
              </Box>
            )}
          </Box>

          {/* This University is not ranked in the above ranking system. */}
          <Box className="mt-4">
            <Checkbox
              disabled={isViewMode}
              label={Ranking.NOT_RANKED}
              value={formik.values.notApplicable}
              name="notApplicable"
              onChange={handleNoRanked}
              error={Boolean(formik.errors.notApplicable)}
              onBlur={formik.handleBlur}
            />
          </Box>
          {!isViewMode && (
            <div className="save-area">
              <Button variant="contained" color="primary" type="submit">
                {PAGE_TEXT.SAVE}
              </Button>
              {/* show in the last degree */}
              {onJumpToNext && (
                <Button className="!ml-4" onClick={onJumpToNext}>
                  {PAGE_TEXT.NEXT}
                </Button>
              )}
            </div>
          )}
        </Stack>
      </form>
    </ContentBox>
  );
};

const ContentBox = styled.div`
  .func-area {
    margin-left: 0.5rem;
    border-radius: 16px;
    cursor: pointer;
    box-shadow: 0.5px 0.5px 0.5px 0.5px rgba(0, 0, 0, 0.25);
  }

  hr {
    margin-top: 1rem;
    margin-bottom: 1rem;
    background-image: linear-gradient(to right, #fff, #ccc, #fff);
  }

  .noPadding {
    padding-left: 2 !important;
  }

  .save-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    margin-top: 2rem;
    margin-bottom: 1rem;
  }
`;
